<template>
  <MorphIcon
    v-bind="forward"
    :class="rootClass"
    :style="rootStyle"
    :size="pixelSize"
    :color="color || 'currentColor'"
    :stroke-width="resolvedStrokeWidth"
    :absolute-stroke-width="absoluteStrokeWidth"
    :spring="spring"
    :reduced-motion="reducedMotion"
    :label="label"
  />
</template>

<script setup>
import { computed } from 'vue';
import { MorphIcon } from 'morphicons/vue';
import { pmMorphStrokeWidthForSize } from './morphicon-defaults.js';
import { getPmMorphIconData, hasPmMorphIcon, resolvePmMorphIconInput } from './pm-morph-icon-map.js';

const SIZE_PX = {
  sm: 16,
  md: 16,
  lg: 20,
  xl: 24,
  '2xl': 32,
};

const props = defineProps({
  /** 项目映射名（见 pm-morph-icon-map.js）；业务代码必须用此 prop，勿用 Lucide 原名 */
  name: {
    type: String,
    default: undefined,
  },
  /** 低层 IconNode / path；仅 /icon-morph 预览或映射表内部，业务页勿用 */
  icon: {
    type: [String, Array],
    default: undefined,
  },
  from: {
    type: [String, Array],
    default: undefined,
  },
  to: {
    type: [String, Array],
    default: undefined,
  },
  progress: {
    type: Number,
    default: undefined,
  },
  spring: {
    type: [String, Object],
    default: 'snappy',
  },
  reducedMotion: {
    type: String,
    default: 'user',
  },
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg', 'xl', '2xl'].includes(v),
  },
  color: {
    type: String,
    default: '',
  },
  /** 不传则按尺寸自动：16→1.25，24→1.5，中间线性插值 */
  strokeWidth: {
    type: [Number, String],
    default: undefined,
  },
  absoluteStrokeWidth: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: undefined,
  },
});

if (process.env.NODE_ENV !== 'production' && props.name && !hasPmMorphIcon(props.name)) {
  console.warn(
    `[PmMorphIcon] unknown mapped name: "${props.name}". Add to pm-morph-icon-map.js (project name, not Lucide name).`,
  );
}

const pixelSize = computed(() => SIZE_PX[props.size] || 16);

const resolvedStrokeWidth = computed(() => {
  if (props.strokeWidth !== undefined && props.strokeWidth !== '') {
    return props.strokeWidth;
  }
  return pmMorphStrokeWidthForSize(pixelSize.value);
});

const resolvedIcon = computed(() => {
  if (props.name) return getPmMorphIconData(props.name);
  return resolvePmMorphIconInput(props.icon);
});

const resolvedFrom = computed(() => resolvePmMorphIconInput(props.from));
const resolvedTo = computed(() => resolvePmMorphIconInput(props.to));

const rootClass = computed(() => ['pm-morph-icon', `pm-morph-icon--${props.size}`]);

const rootStyle = computed(() => {
  if (!props.color) return undefined;
  return { color: props.color };
});

const forward = computed(() => {
  const payload = {};
  const icon = resolvedIcon.value;
  if (icon !== undefined) payload.icon = icon;
  if (props.from !== undefined) payload.from = resolvedFrom.value;
  if (props.to !== undefined) payload.to = resolvedTo.value;
  if (props.progress !== undefined) payload.progress = props.progress;
  return payload;
});
</script>

<style lang="scss" scoped>
.pm-morph-icon {
  display: inline-block;
  vertical-align: -0.1em;
  flex-shrink: 0;
}
</style>
